import * as Sentry from '@sentry/react'
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from 'next'
import { useEffect, useState } from 'react'

import { api } from '@/clients/api'
import allMarkets from '@/clients/markets.json'
import { ViewRouter } from '@/components/view-router/view-router'
import { AppContextProvider } from '@/contexts/app-context/app-context'
import { env } from '@/env.mjs'
import * as Buyback from '@/features/circular/buyback'
import i18n from '@/i18n/config'

function MarketPage({
  configuration = [],
  country_code,
  language_code,
  ikeaScriptUrls = [],
  optimizelySdkKey = '',
  stores,
  market,
  buybackMarkets,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  useState(() => {
    i18n.changeLanguage(market)
  })
  // add country_code and language_code to sentry tags
  useEffect(() => {
    Sentry.setTag('country_code', country_code)
    Sentry.setTag('language_code', language_code)
  }, [country_code, language_code])

  return (
    <AppContextProvider
      configuration={configuration}
      country_code={country_code}
      language_code={language_code}
      ikeaScriptUrls={ikeaScriptUrls}
      optimizelySdkKey={optimizelySdkKey}
      stores={stores}
      buybackMarkets={buybackMarkets}
    >
      <ViewRouter />
    </AppContextProvider>
  )
}

export const getStaticProps = (async ({ params }) => {
  if (!params?.market) {
    throw new Error(`missing market param`)
  }

  if (Array.isArray(params.market)) {
    throw new Error(`market is an array but should be a string`)
  }

  if (!env.NEXT_PUBLIC_APP_URL) {
    throw new Error(`missing APP_URL env variable`)
  }

  const optimizelySdkKey = env.NEXT_PUBLIC_OPTIMIZELY_SDK_KEY || ''

  const [country_code, language_code] = params.market.split('-')
  let ikeaScriptUrls: string[] = []

  // Fetch existing buyback markets
  const buybackMarkets = await Buyback.getMarkets()

  if (env.NEXT_PUBLIC_ENV !== 'PRO') {
    const ikeaDomain =
      env.NEXT_PUBLIC_ENV === 'PRE' ? 'www.cte.ikeadt.com' : 'www.ikea.com'
    const url = `https://${ikeaDomain}/${country_code}/${language_code}/ikea-abtesting/header-scripts-fragment.html`
    console.time(`header-scripts-fragment: ${url}`)
    const headerFragment = await fetch(url)
    console.timeEnd(`header-scripts-fragment: ${url}`)
    if (headerFragment.ok) {
      const responseRawScript = await headerFragment.text()
      const parsedScriptUrls = responseRawScript
        .match(/"(.*?)"/g)
        ?.map((item) => item.replace(/"/g, ''))
      if (parsedScriptUrls) {
        ikeaScriptUrls = parsedScriptUrls
      }
    }
  }

  try {
    console.time(`${country_code}-${language_code}: config fetching`)
    const configResponse = await api.configuration.get({
      country_code,
      language_code,
    })
    console.timeEnd(`${country_code}-${language_code}: config fetching`)

    // Fetch reservation policies directly from reservation service
    console.time(
      `${country_code}-${language_code}: reservation policy fetching`
    )
    let reservationPolicyConfigs: typeof configResponse.data = []
    try {
      const reservationPolicy = await api.reservationPolicy.get({
        countryCode: country_code,
      })
      // Convert reservation policy to configuration format
      reservationPolicyConfigs = [
        {
          id: -1,
          country_code,
          type: 'MODULE',
          module: 'CORE',
          property: 'concurrent_reservation_limit',
          value: String(reservationPolicy.maxActiveReservations),
          visibility: 'PUBLIC',
        },
        {
          id: -2,
          country_code,
          type: 'MODULE',
          module: 'CORE',
          property: 'offer_reservation_limit',
          value: String(reservationPolicy.maxReservationsPerOffer),
          visibility: 'PUBLIC',
        },
      ]
    } catch (policyError) {
      console.warn(
        `Failed to fetch reservation policy for ${country_code}:`,
        policyError
      )
    }
    console.timeEnd(
      `${country_code}-${language_code}: reservation policy fetching`
    )

    console.time(`${country_code}-${language_code}: stores fetching`)
    const stores = await api.stores.get(country_code)
    console.timeEnd(`${country_code}-${language_code}: stores fetching`)

    // Merge configuration with reservation policies
    const configuration = [
      ...(configResponse?.data || []),
      ...reservationPolicyConfigs,
    ]

    return {
      props: {
        market: params.market,
        country_code,
        language_code,
        configuration,
        stores,
        ikeaScriptUrls,
        optimizelySdkKey,
        buybackMarkets,
      },
    }
  } catch (error) {
    console.error(error)
    return {
      notFound: true,
    }
  }
}) satisfies GetStaticProps

export const getStaticPaths: GetStaticPaths = async () => {
  const allowedMarkets = env.NEXT_PUBLIC_MARKETS
    ? env.NEXT_PUBLIC_MARKETS.split(',')
    : allMarkets
  const markets = allMarkets.filter((m: string) => allowedMarkets.includes(m))

  return {
    paths: markets.map((market: string) => ({ params: { market } })),
    fallback: false,
  }
}

export default MarketPage
